/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useWBL } from '../context/WBLContext';
import { GraduationCap, LogIn, X, Mail, Lock, User, MapPin, Eye, EyeOff, AlertCircle, Loader2, ChevronDown, Briefcase, ShieldCheck, Users, Search, BookOpen, Clock, Heart, ArrowRight, CheckCircle2, HelpCircle, MessageSquare, Scale, Send, Pause, Play, ExternalLink } from 'lucide-react';
import { UserRole } from '../types';
import { SetupWizard } from './Onboarding/SetupWizard';
import { LicenseAttributionModal } from './Common/LicenseAttributionModal';
import { useInAppDialog } from './Common/InAppDialog';
import { SCMap } from './SCMap';
import { ProfessionalOrgFallback } from './Common/ProfessionalOrgFallback';
import { collection, addDoc } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import studentWblImg from '../assets/images/student_wbl_opportunity_1782528661429.jpg';
import employerWblImg from '../assets/images/employer_wbl_manufacturing_1782529104092.jpg';
import parentWblImg from '../assets/images/parent_wbl_clinical_1782529114780.jpg';
import advisorWblImg from '../assets/images/advisor_wbl_presentation_1782529126890.jpg';

const getRegionDarkBg = (regionId: string) => {
  const deepBgs: Record<string, string> = {
    'spartanburg': '#03142c', // Elegant Deep navy for Spartanburg County
    'union': '#0a1024',
    'charleston': '#021822',  // Ocean dark for Charleston County
    'greenville': '#042211',  // Forest deep dark for Greenville
    'richland': '#18022d',    // Deep violet for Richland
    'horry': '#261502',       // Deep sunset gold for Horry
    'lexington': '#240212',   // Deep wine for Lexington
    'beaufort': '#071626',
  };
  return deepBgs[regionId.toLowerCase()] || '#091526'; // Standard deep slate/navy
};

const getRegionThemeAccent = (regionId: string) => {
  const themes: Record<string, string> = {
    'spartanburg': '#b9e612',
    'charleston': '#38bdf8',
    'greenville': '#10b981',
    'richland': '#8b5cf6',
    'horry': '#f59e0b',
    'lexington': '#ec4899',
    'beaufort': '#38bdf8',
  };
  return themes[regionId.toLowerCase()] || '#b9e612';
};

const defaultSponsor1Svg = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 80" width="100%" height="100%">
  <text x="20" y="32" font-family="-apple-system, system-ui, sans-serif" font-size="11" font-weight="800" fill="%23ffffff" letter-spacing="4">POWERED BY</text>
  <text x="20" y="60" font-family="-apple-system, system-ui, sans-serif" font-size="22" font-weight="900" fill="%23ffffff" letter-spacing="1">MOVEMENT <tspan font-weight="300" fill="%23ffffff">2030</tspan></text>
</svg>
`)}`;

const defaultSponsor2Svg = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 80" width="100%" height="100%">
  <text x="20" y="32" font-family="-apple-system, system-ui, sans-serif" font-size="11" font-weight="800" fill="%23ffffff" letter-spacing="3">AN INITIATIVE OF</text>
  <text x="20" y="60" font-family="-apple-system, system-ui, sans-serif" font-size="20" font-weight="900" fill="%23ffffff" letter-spacing="1.5">ONESPARTANBURG <tspan font-weight="300" fill="%23ffffff">INC.</tspan></text>
</svg>
`)}`;

interface RegionScrollableSectionProps {
  regionId: string;
  regionName: string;
}

const RegionScrollableSection: React.FC<RegionScrollableSectionProps> = ({ regionId, regionName }) => {
  const { tenants, globalBranding } = useWBL();
  const darkBg = getRegionDarkBg(regionId);
  const regionTheme = React.useMemo(() => {
    const themes: Record<string, { primary: string, accent: string }> = {
      'spartanburg': { primary: '#0094c8', accent: '#b9e612' },
      'charleston': { primary: '#38bdf8', accent: '#38bdf8' },
      'greenville': { primary: '#10b981', accent: '#10b981' },
      'richland': { primary: '#8b5cf6', accent: '#8b5cf6' },
      'horry': { primary: '#f59e0b', accent: '#f59e0b' },
      'lexington': { primary: '#ec4899', accent: '#ec4899' }
    };
    return themes[regionId.toLowerCase()] || { primary: '#4f46e5', accent: '#b9e612' };
  }, [regionId]);

  const currentRegionTenant = tenants?.find(t => t.id.toLowerCase() === regionId.toLowerCase());
  const secondaryLogo = currentRegionTenant?.logos?.secondary || globalBranding?.logos?.secondary;

  const customPersonaSections = currentRegionTenant?.personaSections;
  
  const getPersonaSection = (id: 'student' | 'employer' | 'parent' | 'advisor') => {
    const custom = customPersonaSections?.find(s => s.id === id);
    if (custom) {
      return {
        ...custom,
        image: custom.image || (id === 'student' ? studentWblImg : id === 'employer' ? employerWblImg : id === 'parent' ? parentWblImg : advisorWblImg)
      };
    }
    
    // Default fallback mappings
    switch (id) {
      case 'student':
        return {
          id: 'student' as const,
          label: 'STUDENT HUB',
          title: 'Launch Your Career',
          description: 'Step into the professional arena with total clarity. Explore modern work-based learning pathways tailored to your skills, secure mentored internships, earn industry-recognized credentials, and begin building a powerhouse career file before graduation.',
          image: studentWblImg,
          quote: '"This experience gives me actual industry-grade practice before starting medical school."',
          author: 'Alex Rivera',
          authorInitials: 'AR',
          authorDetails: 'Allied Health Intern • ApexTech Medical',
          items: [
            'Discover premium local internships matching your interests',
            'Gain digital badges to certify high-demand skills',
            'Log program hours with seamless mobile timesheets'
          ]
        };
      case 'employer':
        return {
          id: 'employer' as const,
          label: 'EMPLOYER PLATFORM',
          title: 'Secure Your Pipeline',
          description: 'Connect your business straight to local classrooms. Post internship opportunities, review verified student applications, approve mobile timesheets, evaluate safety guidelines, and recruit highly motivated, pre-screened regional talent instantly.',
          image: employerWblImg,
          quote: '"The hands-on co-op placement gives our business an active, pre-vetted local talent pool."',
          author: 'Vince K.',
          authorInitials: 'BM',
          authorDetails: 'Senior Assembly Lead • BMW Manufacturing Hub',
          items: [
            'Host students in flexible, academic-backed placements',
            'Simplify tracking with secure, one-click evaluation logs',
            'Boost community visibility as an elite education partner'
          ]
        };
      case 'parent':
        return {
          id: 'parent' as const,
          label: 'PARENT GUARDIAN PORTAL',
          title: 'Guide your Student',
          description: "Be an active participant in your child's success. Track safety compliance protocols, view real-time logbooks, review training agreements, grant worksite permissions, and communicate and align directly with school coordinators on a secure, clear platform.",
          image: parentWblImg,
          quote: '"The portal gives me absolute peace of mind knowing my daughter\'s clinical internship is fully vetted and safe."',
          author: 'Maria L.',
          authorInitials: 'ML',
          authorDetails: 'Parent Guardian • Allied Health Track',
          items: [
            'Ensure placements meet high safety and labor standards',
            'Review and authorize internship agreements with ease',
            'Monitor timesheets, feedback, and milestones in real-time'
          ]
        };
      case 'advisor':
        return {
          id: 'advisor' as const,
          label: 'ADVISOR CENTRAL',
          title: 'Streamline Success',
          description: 'Coordinate and align educational pathways perfectly with industry requirements. Easily matching certified students with host employers, routing permission documents to parents, endorsing learning logs, and viewing high-impact analytical metrics.',
          image: advisorWblImg,
          quote: '"Unifying students, employers, and schools creates a massive competitive advantage for our county\'s future."',
          author: 'Teresa C.',
          authorInitials: 'TC',
          authorDetails: 'Regional CTE Pathways Director',
          items: [
            'Track student compliance and safety standards countywide',
            'Digitally manage evaluation agreements and timesheet reviews',
            'Foster real connection between business hosts and schools'
          ]
        };
    }
  };

  const studentSec = getPersonaSection('student');
  const employerSec = getPersonaSection('employer');
  const parentSec = getPersonaSection('parent');
  const advisorSec = getPersonaSection('advisor');

  return (
    <div 
      className="w-full flex flex-col relative overflow-visible pt-20 md:pt-28"
      style={{ backgroundColor: darkBg }}
    >
      {secondaryLogo && (
        <div className="w-full max-w-6xl mx-auto px-4 md:px-8 flex justify-center items-center pt-6 pb-12 md:pb-16 animate-fade-in">
          <div className="relative group flex justify-center">
            <div className="absolute -inset-4 bg-white/5 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition duration-500" />
            <img 
              src={secondaryLogo} 
              alt={`${currentRegionTenant?.name || regionName} Secondary Logo`}
              className="h-40 md:h-[200px] w-auto object-contain relative z-10 transition-transform duration-500 hover:scale-[1.03] mx-auto"
              referrerPolicy="no-referrer"
            />
          </div>
        </div>
      )}


        {/* 4 DYNAMIC PERSONA SECTIONS WITH IMPRESSIVE INDEPENDENT MOVEMENT */}
        <div className="w-full max-w-6xl mx-auto space-y-32 px-4 md:px-8 mt-6 text-left pb-16 relative z-10">
          
          {/* Section 1: Students */}
          <motion.div 
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
          >
            {/* Column 1: Info */}
            <div className="lg:col-span-5 space-y-6">
              <div 
                className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full border text-xs font-bold tracking-widest uppercase"
                style={{ borderColor: `${regionTheme.accent}33`, backgroundColor: `${regionTheme.accent}14`, color: regionTheme.accent }}
              >
                <GraduationCap className="w-4 h-4" />
                <span>{studentSec.label}</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight leading-tight">
                {studentSec.title}
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                {studentSec.description}
              </p>
              
              <ul className="space-y-3.5 pt-2">
                {studentSec.items.map((item, i) => (
                  <li key={i} className="flex items-start space-x-2.5 text-xs text-slate-300 font-medium">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: regionTheme.accent }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 2: High-impact Student Internship Image Showcase */}
            <div className="lg:col-span-7 relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/20 to-indigo-500/10 rounded-3xl blur-xl opacity-75 group-hover:opacity-100 transition duration-1000" />
              
              <div className="relative border border-white/10 bg-slate-950 rounded-3xl shadow-2xl overflow-hidden aspect-[4/3] md:aspect-[16/10] flex items-end">
                {/* Image */}
                <img 
                  src={studentSec.image} 
                  alt="Student in Work-Based Learning Opportunity" 
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-1000 ease-out brightness-90"
                  referrerPolicy="no-referrer"
                />
                
                {/* Overlay Gradients */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent opacity-90" />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-950/20 via-transparent to-slate-950/20" />



                {/* Bottom Overlay Text */}
                <div className="relative z-10 p-6 md:p-8 w-full space-y-2 text-left">

                  <h4 className="text-lg md:text-xl font-bold text-white tracking-tight leading-snug">
                    {studentSec.quote}
                  </h4>
                  <div className="flex items-center space-x-3 pt-1">
                    <div className="w-8 h-8 rounded-lg bg-[#0094c8]/20 flex items-center justify-center font-bold text-white text-xs border border-[#0094c8]/30">
                      {studentSec.authorInitials}
                    </div>
                    <div>
                      <p className="text-xs font-black text-white uppercase tracking-wider leading-none">{studentSec.author}</p>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">{studentSec.authorDetails}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 2: Employers */}
          <motion.div 
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
          >
            {/* Column 1: High-impact Employer WBL Image Showcase */}
            <div className="lg:col-span-7 lg:order-last lg:order-none relative group lg:mr-4">
              <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/20 to-teal-500/10 rounded-3xl blur-xl opacity-75 group-hover:opacity-100 transition duration-1000" />
              
              <div className="relative border border-white/10 bg-slate-950 rounded-3xl shadow-2xl overflow-hidden aspect-[4/3] md:aspect-[16/10] flex items-end">
                {/* Image */}
                <img 
                  src={employerSec.image} 
                  alt="Student in Advanced Manufacturing WBL Co-Op" 
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-1000 ease-out brightness-90"
                  referrerPolicy="no-referrer"
                />
                
                {/* Overlay Gradients */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent opacity-90" />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-950/20 via-transparent to-slate-950/20" />



                {/* Bottom Overlay Text */}
                <div className="relative z-10 p-6 md:p-8 w-full space-y-2 text-left">

                  <h4 className="text-lg md:text-xl font-bold text-white tracking-tight leading-snug">
                    {employerSec.quote}
                  </h4>
                  <div className="flex items-center space-x-3 pt-1">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center font-bold text-white text-xs border border-emerald-500/30">
                      {employerSec.authorInitials}
                    </div>
                    <div>
                      <p className="text-xs font-black text-white uppercase tracking-wider leading-none">{employerSec.author}</p>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">{employerSec.authorDetails}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Column 2: Info (placed on right) */}
            <div className="lg:col-span-5 space-y-6">
              <div 
                className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full border text-xs font-bold tracking-widest uppercase"
                style={{ borderColor: `${regionTheme.accent}33`, backgroundColor: `${regionTheme.accent}14`, color: regionTheme.accent }}
              >
                <Briefcase className="w-4 h-4" />
                <span>{employerSec.label}</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight leading-tight">
                {employerSec.title}
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                {employerSec.description}
              </p>
              
              <ul className="space-y-3.5 pt-2">
                {employerSec.items.map((item, i) => (
                  <li key={i} className="flex items-start space-x-2.5 text-xs text-slate-300 font-medium">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: regionTheme.accent }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Section 3: Parents */}
          <motion.div 
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
          >
            {/* Column 1: Info */}
            <div className="lg:col-span-5 space-y-6">
              <div 
                className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full border text-xs font-bold tracking-widest uppercase"
                style={{ borderColor: `${regionTheme.accent}33`, backgroundColor: `${regionTheme.accent}14`, color: regionTheme.accent }}
              >
                <Heart className="w-4 h-4" />
                <span>{parentSec.label}</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight leading-tight">
                {parentSec.title}
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                {parentSec.description}
              </p>
              
              <ul className="space-y-3.5 pt-2">
                {parentSec.items.map((item, i) => (
                  <li key={i} className="flex items-start space-x-2.5 text-xs text-slate-300 font-medium">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: regionTheme.accent }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 2: High-impact Parents WBL Image Showcase */}
            <div className="lg:col-span-7 relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-pink-500/20 to-purple-500/10 rounded-3xl blur-xl opacity-75 group-hover:opacity-100 transition duration-1000" />
              
              <div className="relative border border-white/10 bg-slate-950 rounded-3xl shadow-2xl overflow-hidden aspect-[4/3] md:aspect-[16/10] flex items-end">
                {/* Image */}
                <img 
                  src={parentSec.image} 
                  alt="Student in Allied Health Clinical Placement" 
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-1000 ease-out brightness-90"
                  referrerPolicy="no-referrer"
                />
                
                {/* Overlay Gradients */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent opacity-90" />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-950/20 via-transparent to-slate-950/20" />



                {/* Bottom Overlay Text */}
                <div className="relative z-10 p-6 md:p-8 w-full space-y-2 text-left">

                  <h4 className="text-lg md:text-xl font-bold text-white tracking-tight leading-snug">
                    {parentSec.quote}
                  </h4>
                  <div className="flex items-center space-x-3 pt-1">
                    <div className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center font-bold text-white text-xs border border-pink-500/30">
                      {parentSec.authorInitials}
                    </div>
                    <div>
                      <p className="text-xs font-black text-white uppercase tracking-wider leading-none">{parentSec.author}</p>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">{parentSec.authorDetails}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 4: Advisors & Partners */}
          <motion.div 
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
          >
            {/* Column 1: High-impact Advisors WBL Image Showcase */}
            <div className="lg:col-span-7 lg:order-last lg:order-none relative group lg:mr-4">
              <div className="absolute -inset-4 bg-gradient-to-r from-violet-500/20 to-indigo-500/10 rounded-3xl blur-xl opacity-75 group-hover:opacity-100 transition duration-1000" />
              
              <div className="relative border border-white/10 bg-slate-950 rounded-3xl shadow-2xl overflow-hidden aspect-[4/3] md:aspect-[16/10] flex items-end">
                {/* Image */}
                <img 
                  src={advisorSec.image} 
                  alt="Student presenting project to advisor" 
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-1000 ease-out brightness-90"
                  referrerPolicy="no-referrer"
                />
                
                {/* Overlay Gradients */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent opacity-90" />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-950/20 via-transparent to-slate-950/20" />



                {/* Bottom Overlay Text */}
                <div className="relative z-10 p-6 md:p-8 w-full space-y-2 text-left">

                  <h4 className="text-lg md:text-xl font-bold text-white tracking-tight leading-snug">
                    {advisorSec.quote}
                  </h4>
                  <div className="flex items-center space-x-3 pt-1">
                    <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center font-bold text-white text-xs border border-violet-500/30">
                      {advisorSec.authorInitials}
                    </div>
                    <div>
                      <p className="text-xs font-black text-white uppercase tracking-wider leading-none">{advisorSec.author}</p>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">{advisorSec.authorDetails}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Column 2: Info */}
            <div className="lg:col-span-5 space-y-6">
              <div 
                className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full border text-xs font-bold tracking-widest uppercase"
                style={{ borderColor: `${regionTheme.accent}33`, backgroundColor: `${regionTheme.accent}14`, color: regionTheme.accent }}
              >
                <Users className="w-4 h-4" />
                <span>{advisorSec.label}</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight leading-tight">
                {advisorSec.title}
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                {advisorSec.description}
              </p>
              
              <ul className="space-y-3.5 pt-2">
                {advisorSec.items.map((item, i) => (
                  <li key={i} className="flex items-start space-x-2.5 text-xs text-slate-300 font-medium">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: regionTheme.accent }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

        </div>
    </div>
  );
};

export const LandingPage: React.FC = () => {
  const { signInWithGoogle, signUpWithEmail, signInWithEmail, activeUser, activeTenant, tenants, globalBranding, changeTenant, systemConfig, setCurrentView } = useWBL();
  const { showAlert } = useInAppDialog();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.altKey && event.key === 's') {
        changeTenant('training');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [changeTenant]);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'login' | 'signup'>('login');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [isSubmittingInquiry, setIsSubmittingInquiry] = useState(false);
  const [inquirySubmitted, setInquirySubmitted] = useState(false);
  const [inquiryError, setInquiryError] = useState('');
  
  const isRegionActive = React.useMemo(() => {
    // If multi-tenant is disabled, we consider a region active if one is selected
    if (systemConfig && !systemConfig.multiTenantEnabled && systemConfig.selectedRegionId !== 'global') {
      return true;
    }

    return activeTenant && 
      activeTenant.id !== 'global' && 
      activeTenant.id !== 'apextech' && 
      
      activeTenant.id !== 'springfield' && 
      activeTenant.id !== 'valleyhealth';
  }, [activeTenant, systemConfig]);

  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);

  const toggleLandingVideo = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const video = videoRef.current;
    if (video) {
      if (video.paused) {
        video.play().then(() => setIsVideoPlaying(true)).catch(() => {});
      } else {
        video.pause();
        setIsVideoPlaying(false);
      }
    }
  };

  const [participatingOrgs, setParticipatingOrgs] = useState<any[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState<boolean>(true);

  const carouselOrgs = React.useMemo(() => {
    if (participatingOrgs.length === 0) return [];
    let list = [...participatingOrgs];
    while (list.length < 12) {
      list = [...list, ...participatingOrgs];
    }
    return [...list, ...list];
  }, [participatingOrgs]);

  React.useEffect(() => {
    if (!activeTenant?.id) {
      setParticipatingOrgs([]);
      setLoadingOrgs(false);
      return;
    }

    let isMounted = true;
    setLoadingOrgs(true);

    const loadOrgs = async () => {
      try {
        let allOrgs: any[] = [];
        const config = await import('../firebase-applet-config.json');
        
        // Detect if we are using mock keys
        const isMockActive = !config.apiKey || 
          config.apiKey.includes('mock-api-key') || 
          config.projectId.includes('mock-app') ||
          config.projectId.includes('wbl-portal-mock');

        if (isMockActive) {
          const { MOCK_USERS } = await import('../data/mockData');
          allOrgs = MOCK_USERS.filter(u => u.role === 'organization');
        } else {
          try {
            const { collection, getDocs, query, where, limit } = await import('firebase/firestore');
            const { db } = await import('../firebase');
            const q = query(
              collection(db, 'users'), 
              where('role', '==', 'organization'),
              limit(25)
            );
            const querySnapshot = await getDocs(q);
            const list: any[] = [];
            querySnapshot.forEach(doc => {
              list.push(doc.data());
            });
            allOrgs = list;
          } catch (orgQueryErr) {
            console.warn("Notice: Operating in fallback mode for organization carousel query:", orgQueryErr);
            const { MOCK_USERS } = await import('../data/mockData');
            allOrgs = MOCK_USERS.filter(u => u.role === 'organization');
          }
        }

        const activeRegionId = (activeTenant.id || '').toLowerCase();
        const activeRegionName = (activeTenant.name || '').toLowerCase();

        // STRICT REGIONAL FILTER & SUPERADMIN APPEARANCE OVERSIGHT: Only orgs belonging to activeRegionId with showInParticipatingOrgs: true
        const regionOnlyOrgs = allOrgs.filter(org => {
          const tenantMatch = (org.tenantId || '').toLowerCase() === activeRegionId;
          const extendedMatch = Array.isArray(org.extendedRegions) && org.extendedRegions.some(r => r.toLowerCase() === activeRegionId);
          const regionNameMatch = (org.region || '').toLowerCase().includes(activeRegionName) || (org.region || '').toLowerCase().includes(activeRegionId);
          const isApproved = org.showInParticipatingOrgs === true;
          return (tenantMatch || extendedMatch || regionNameMatch) && isApproved;
        });

        // ONLY USE THE LEAD - deduplicate by organization name and prioritize isOrgLead / isLead
        const orgMap = new Map<string, any>();
        regionOnlyOrgs.forEach(org => {
          const key = (org.orgName || org.name || '').toLowerCase().trim();
          if (!key) return;
          const existing = orgMap.get(key);
          const isCurrentLead = org.isOrgLead === true || org.isLead === true;
          if (!existing) {
            orgMap.set(key, org);
          } else {
            const existingIsLead = existing.isOrgLead === true || existing.isLead === true;
            if (isCurrentLead && !existingIsLead) {
              orgMap.set(key, org);
            }
          }
        });
        const deduplicatedOrgs = Array.from(orgMap.values());


        // Also retrieve regional opportunities to collect any partner organizations hosting opportunities in this active region
        let regionalOppOrgs: any[] = [];
        if (isMockActive) {
          const { MOCK_OPPORTUNITIES } = await import('../data/mockData');
          regionalOppOrgs = MOCK_OPPORTUNITIES.filter(o => (o.tenantId || '').toLowerCase() === activeRegionId);
        } else {
          try {
            const { collection, getDocs, query, where, limit } = await import('firebase/firestore');
            const { db } = await import('../firebase');
            const q = query(
              collection(db, 'opportunities'),
              where('tenantId', '==', activeTenant.id),
              limit(20)
            );
            const querySnapshot = await getDocs(q);
            querySnapshot.forEach(doc => {
              regionalOppOrgs.push(doc.data());
            });
          } catch (e) {
            console.warn("Could not query regional opportunity orgs:", e);
          }
        }

        regionalOppOrgs.forEach(opp => {
          const orgName = opp.organizationName || opp.orgName;
          if (orgName && !deduplicatedOrgs.some(o => (o.orgName || o.name || '').toLowerCase() === orgName.toLowerCase())) {
            deduplicatedOrgs.push({
              uid: opp.organizationId || `opp-org-${orgName.replace(/[^a-z0-9]/gi, '')}`,
              orgName,
              tenantId: activeTenant.id,
              industry: opp.careerCluster || opp.subcluster,
              avatar: opp.orgLogo || opp.avatar,
              website: opp.website,
              showInParticipatingOrgs: true
            });
          }
        });

        // Ensure website URLs are properly formatted for region-only organizations
        const finalRegionOrgs = deduplicatedOrgs.map(org => {
          let website = org.website || org.url;
          const name = (org.orgName || org.name || '').toLowerCase();
          if (!website) {
            if (name.includes('apex')) website = 'https://apextech.com';
            else if (name.includes('valley')) website = 'https://www.valleymed.org';
            else if (name.includes('precision')) website = 'https://www.precisiondynamics.com';
            else if (name.includes('upstate auto')) website = 'https://www.upstateautoengineering.com';
            else if (name.includes('upstate healing')) website = 'https://www.upstatehealing.org';
            else if (name.includes('novapack')) website = 'https://www.novapack.com';
            else {
              const cleanDomain = name.replace(/[^a-z0-9]/g, '');
              website = cleanDomain ? `https://www.${cleanDomain}.com` : `https://www.google.com/search?q=${encodeURIComponent(org.orgName || org.name || 'Organization')}`;
            }
          }
          return { ...org, website };
        });

        if (isMounted) {
          setParticipatingOrgs(finalRegionOrgs);
          setLoadingOrgs(false);
        }
      } catch (err) {
        console.error("Error loading regional participating organizations:", err);
        if (isMounted) {
          setLoadingOrgs(false);
        }
      }
    };

    loadOrgs();

    return () => {
      isMounted = false;
    };
  }, [activeTenant?.id]);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [initialSignupRole, setInitialSignupRole] = useState<UserRole | null>(null);
  const [signupStep, setSignupStep] = useState<'role-selection' | 'auth'>('role-selection');
  const [authError, setAuthError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Store returning user flag upon successful login but do not auto-open drawer
  React.useEffect(() => {
    if (activeUser) {
      const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
      const isJoinOrg = params.get('joinOrg') === '1' || params.get('orgInvite') === 'true';
      if (!isJoinOrg) {
        localStorage.setItem('wbl_returning_user', 'true');
        setDrawerOpen(false);
      }
    }
  }, [activeUser]);

  // Auto-open pre-filled signup popout drawer when invited to join an existing organization via URL parameters
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const isJoinOrg = params.get('joinOrg') === '1' || params.get('orgInvite') === 'true';
      if (isJoinOrg) {
        const primaryRegion = params.get('primaryRegion') || params.get('region') || '';
        if (primaryRegion && activeTenant?.id !== primaryRegion) {
          const matchedTenant = tenants.find(t => t.id === primaryRegion || t.subdomain === primaryRegion);
          if (matchedTenant) {
            changeTenant(matchedTenant.id);
          }
        }
        setInitialSignupRole('organization');
        setDrawerMode('signup');
        setDrawerOpen(true);
      }
    }
  }, [activeTenant?.id, changeTenant, tenants]);

  const openDrawer = (mode: 'login' | 'signup') => {
    setDrawerMode(mode);
    setDrawerOpen(true);
    setAuthError(null);
  };

  useEffect(() => {
    (window as any).openSignupDrawer = (role: UserRole) => {
        setInitialSignupRole(role);
        openDrawer('signup');
    };
  }, []);


  const handleGoogleAuth = async () => {
    setAuthError(null);
    setIsSubmitting(true);
    try {
      await signInWithGoogle();
      setDrawerOpen(false);
    } catch(e: any) {
      console.warn("Auth warning:", e.message);
      let errorMessage = "Google authentication failed.";
      if (e.code === 'auth/popup-closed-by-user') {
        errorMessage = "Google sign-in popup was closed before completion.";
      } else if (e.code === 'auth/network-request-failed') {
        errorMessage = "Network error. Please check your connection.";
      } else if (e.message) {
        errorMessage = e.message;
      }
      setAuthError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAuth = async () => {
    setAuthError(null);
    setIsSubmitting(true);
    try {
      if (drawerMode === 'signup') {
        // Simple auth for signup, onboarding happens automatically in App.tsx
        await signUpWithEmail(email, password, 'student'); 
      } else {
        await signInWithEmail(email, password);
      }
      setDrawerOpen(false);
      setCurrentView('dashboard');
    } catch (e: any) {
      console.warn("Auth warning:", e.message);
      let errorMessage = "Authentication failed. Please check your credentials and try again.";
      if (e.code === 'auth/invalid-credential' || e.code === 'auth/user-not-found' || e.code === 'auth/wrong-password' || e.message?.toLowerCase().includes('invalid email or password')) {
        errorMessage = "Invalid email or password. Please check your credentials or click 'Forgot?' to reset.";
      } else if (e.code === 'auth/too-many-requests') {
        errorMessage = "Access to this account has been temporarily disabled due to many failed login attempts. You can immediately restore it by resetting your password or you can try again later.";
      } else if (e.code === 'auth/email-already-in-use') {
         errorMessage = "This email is already in use by another account.";
      } else if (e.message) {
         errorMessage = e.message;
      }
      setAuthError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex flex-col font-sans overflow-x-hidden scroll-smooth">
      {/* Premium Hero Media Background: Renders live looped MP4/WebM video if saved; defaults to static overlay images */}
      <div className="fixed inset-0 z-0 bg-slate-950 overflow-hidden pointer-events-none">
        {globalBranding?.heroVideo && (
          <video
            ref={videoRef}
            src={globalBranding.heroVideo}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover z-0"
            onPlay={() => setIsVideoPlaying(true)}
            onPause={() => setIsVideoPlaying(false)}
          />
        )}
        <div className="absolute inset-0 bg-slate-950/45 z-10" />
      </div>

      {/* Discrete pause/play toggle for landing hero video */}
      {globalBranding?.heroVideo && (
        <button
          type="button"
          onClick={toggleLandingVideo}
          aria-label={isVideoPlaying ? "Pause background video" : "Play background video"}
          title={isVideoPlaying ? "Pause background video" : "Play background video"}
          className="fixed bottom-3 right-3 z-50 w-6 h-6 rounded-full bg-black/30 hover:bg-black/60 text-white/60 hover:text-white backdrop-blur-xs border border-white/10 flex items-center justify-center opacity-40 hover:opacity-100 transition-all cursor-pointer pointer-events-auto shadow-xs"
        >
          {isVideoPlaying ? (
            <Pause className="w-2.5 h-2.5" />
          ) : (
            <Play className="w-2.5 h-2.5 fill-current ml-0.5" />
          )}
        </button>
      )}

      <nav className="relative z-15 w-full text-white shrink-0">
        <div className="w-full flex items-center justify-between px-4 md:px-8 py-3 md:py-4">
          <div 
            className="flex items-center space-x-2 cursor-pointer select-none"
            onDoubleClick={() => {
              console.log("Secret training trigger activated");
              changeTenant('training');
            }}
          >
            {globalBranding?.logos?.navigation ? (
              <img 
                src={globalBranding.logos.navigation} 
                alt="Universal Navigation Logo" 
                className="h-6 sm:h-8 max-w-[200px] object-contain transition-all duration-300"
                referrerPolicy="no-referrer"
              />
            ) : (
              <>
                <GraduationCap className="w-6 h-6" />
                <span className="font-bold text-lg tracking-tight">WBL Portal</span>
              </>
            )}
          </div>
          <div className="flex items-center space-x-3 md:space-x-4">
              <button 
                onClick={() => openDrawer('signup')} 
                className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-slate-200 hover:text-white transition-colors cursor-pointer"
              >
                Sign Up
              </button>
              <button onClick={() => openDrawer('login')} className="flex items-center space-x-1 sm:space-x-2 text-[10px] sm:text-xs font-semibold bg-white/20 hover:bg-white/30 backdrop-blur-md px-3 py-1.5 sm:px-4 sm:py-2 rounded-full transition-all">
                  <LogIn className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>Login</span>
              </button>
          </div>
        </div>
      </nav>

      <main className="relative z-10 flex-1 w-full flex flex-col">
        
        {/* FIRST FOLD: Hero / Card content wrapper */}
        <div className="w-full relative flex-1 min-h-[calc(100vh-64px)] md:min-h-[calc(100vh-72px)] flex flex-col justify-center pt-8 md:pt-12 pb-6">
          <div className="max-w-[1150px] mx-auto w-full flex flex-col md:flex-row items-center justify-between px-6 lg:px-12 xl:px-16 pt-5 pb-0 relative gap-8 md:gap-10">
            
            {/* Left Column: Global Logo */}
            <div className="w-full md:w-[38%] lg:w-[34%] flex flex-col items-center md:items-start justify-center text-center md:text-left select-none animate-fade-in pr-4 md:pr-6">
              {globalBranding?.logos?.hero && (
                <img 
                   src={globalBranding.logos.hero} 
                   alt="Universal Hero Logo" 
                   className="w-full max-w-[185px] sm:max-w-[220px] md:max-w-[250px] lg:max-w-[290px] xl:max-w-[310px] h-auto object-contain transition-all duration-300 md:object-left cursor-pointer"
                   referrerPolicy="no-referrer"
                   onDoubleClick={() => {
                     console.log("Secret training trigger activated via hero logo");
                     changeTenant('training');
                   }}
                />
              )}
            </div>

            {/* Right Column: SCMap Hub */}
            <div className="w-full md:w-[62%] lg:w-[66%] flex items-center justify-center md:justify-end animate-fade-in relative z-20">
              <div className="select-none h-full w-full flex items-center justify-center md:justify-end">
                <SCMap 
                  onSelectCounty={(countyId) => changeTenant(countyId)} 
                  onLoginRequest={() => {
                    setInitialSignupRole(null);
                    setDrawerMode('login');
                    setDrawerOpen(true);
                  }}
                />
              </div>
            </div>
          </div>

          {/* CINEMATIC REVEAL of Active Region / Selection prompt spanning all the way across the bottom */}
          <div className="max-w-[1150px] mx-auto w-full px-6 lg:px-12 xl:px-16 mt-5 md:mt-8 lg:mt-10 z-20 flex justify-center">
            <AnimatePresence mode="wait">
              {isRegionActive ? (
                <motion.div 
                  key="active-region"
                  initial={{ width: "120px", opacity: 0, borderRadius: "9999px" }}
                  animate={{ width: "100%", opacity: 1, borderRadius: "12px" }}
                  exit={{ width: "120px", opacity: 0, borderRadius: "9999px" }}
                  transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                  className="w-full flex flex-col md:flex-row items-center justify-between border border-[#b9e612]/30 bg-[#b9e612]/5 px-8 py-4 shadow-lg shadow-[#b9e612]/5 backdrop-blur-sm overflow-hidden"
                >
                  <motion.div 
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                    className="flex items-center space-x-3 text-center md:text-left py-1 md:py-0 shrink-0"
                  >
                    <span className="w-2 h-2 rounded-full bg-[#b9e612] animate-pulse shrink-0" />
                    <p className="text-sm font-black text-[#b9e612] uppercase tracking-widest">
                      Active Region &bull; {activeTenant?.name || 'Regional Portal'}
                    </p>
                  </motion.div>
                  {systemConfig.multiTenantEnabled && (
                    <motion.button 
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5, duration: 0.6 }}
                      onClick={() => changeTenant('global')}
                      className="text-[10px] font-black text-white/50 hover:text-white uppercase tracking-widest mt-2 md:mt-0 hover:underline cursor-pointer transition-colors"
                    >
                      &bull; Change Selected Region &bull;
                    </motion.button>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="select-county"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                  className="w-full flex justify-center border-t border-white/5 pt-8"
                >
                  <p className="text-xs font-semibold text-white/40 uppercase tracking-widest flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-ping" />
                    Select a county on the map to begin
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* SPONSOR BANNER (The elegant line) - Revealed at the bottom of the first fold when a region is active */}
          <AnimatePresence>
            {isRegionActive && (
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 50, opacity: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="absolute bottom-0 left-0 w-full h-[76px] border-t-2 z-35"
                style={{ 
                  borderColor: getRegionThemeAccent(activeTenant.id),
                  backgroundColor: getRegionDarkBg(activeTenant.id),
                  boxShadow: '0 -10px 30px rgba(0,0,0,0.4)'
                }}
              >
                <div className="w-full h-full flex flex-col md:flex-row items-center justify-between px-4 md:px-8">
                  {/* Left: Powered by Sponsor 1 */}
                  <div className="flex items-center space-x-3 text-white py-1 md:py-0">
                    <img 
                      src={activeTenant?.logos?.sponsor1 || globalBranding?.logos?.sponsor1 || defaultSponsor1Svg} 
                      alt="Sponsor 1" 
                      className="h-8 md:h-10 w-auto max-w-[200px] object-contain transition-all hover:brightness-110" 
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = defaultSponsor1Svg;
                      }}
                    />
                  </div>

                  {/* Center: Animated scroll chevron hint */}
                  <div className="hidden md:flex flex-col items-center justify-center text-white/40 hover:text-white/80 transition-colors pointer-events-none">
                    <motion.div
                      animate={{ y: [0, 5, 0] }}
                      transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                    >
                      <ChevronDown className="w-5 h-5 text-white/60" />
                    </motion.div>
                  </div>

                  {/* Right: Sponsor 2 */}
                  <div className="flex items-center space-x-3 text-white py-1 md:py-0">
                    <img 
                      src={activeTenant?.logos?.sponsor2 || globalBranding?.logos?.sponsor2 || defaultSponsor2Svg} 
                      alt="Sponsor 2" 
                      className="h-8 md:h-10 w-auto max-w-[200px] object-contain transition-all hover:brightness-110" 
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = defaultSponsor2Svg;
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* SECOND FOLD: Scrollable regional section with banner running upwards */}
        {isRegionActive && (
          <>
            <RegionScrollableSection regionId={activeTenant.id} regionName={activeTenant.name} />

            {/* PARTICIPATING ORGANIZATIONS AREA - FULL SCREEN WIDE MOVING CAROUSEL */}
            <div className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] bg-white py-10 flex flex-col items-center z-20 text-center border-t-4 border-b-4 border-[#b9e612] overflow-hidden">
              <h3 className="text-xl md:text-2xl font-black tracking-wider uppercase text-[#011d3c] mb-6 font-sans px-4">
                Participating Organizations
              </h3>

              {loadingOrgs ? (
                <div className="flex items-center justify-center space-x-2 py-8">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-300 animate-bounce" />
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-400 animate-bounce delay-75" />
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-500 animate-bounce delay-150" />
                </div>
              ) : participatingOrgs.length === 0 ? (
                <p className="text-xs md:text-sm font-bold uppercase tracking-widest text-slate-400/90 italic py-6 select-none">
                  Partnerships pending for this regional portal.
                </p>
              ) : (
                <div className="relative w-full overflow-hidden py-4 group">
                  {/* Left & Right gradient edge overlays for smooth infinite scrolling transition */}
                  <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 md:w-36 bg-gradient-to-r from-white via-white/80 to-transparent z-10" />
                  <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 md:w-36 bg-gradient-to-l from-white via-white/80 to-transparent z-10" />

                  {/* Infinite Horizontal Carousel Track */}
                  <div className="flex items-center gap-6 md:gap-10 w-max animate-marquee hover:[animation-play-state:paused] px-4">
                    {carouselOrgs.map((org, index) => {
                      const hasImageLogo = org.avatar && (org.avatar.startsWith('http') || org.avatar.startsWith('data:image'));
                      const rawWebsite = org.website || org.url || '';
                      const formattedUrl = rawWebsite.startsWith('http') 
                        ? rawWebsite 
                        : rawWebsite 
                          ? `https://${rawWebsite}`
                          : `https://www.google.com/search?q=${encodeURIComponent((org.orgName || org.name || 'Organization') + ' official site')}`;

                      return (
                        <a 
                          key={`${org.uid || org.orgName}-${index}`} 
                          href={formattedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Visit ${org.orgName || org.name}'s official website`}
                          className="group/card relative w-28 h-28 md:w-36 md:h-36 shrink-0 flex items-center justify-center transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 block cursor-pointer"
                        >
                          {hasImageLogo ? (
                            <div className="w-full h-full bg-white border border-slate-200/90 rounded-2xl p-4 flex items-center justify-center shadow-2xs group-hover/card:shadow-xl group-hover/card:border-blue-400 group-hover/card:ring-2 group-hover/card:ring-blue-200 transition-all duration-300 overflow-hidden relative">
                              <img 
                                src={org.avatar} 
                                alt={org.orgName || org.name} 
                                className="max-h-full max-w-full object-contain filter group-hover/card:brightness-105 transition-all"
                                referrerPolicy="no-referrer"
                              />
                            </div>
                          ) : (
                            <div className="w-full h-full rounded-2xl overflow-hidden shadow-2xs group-hover/card:shadow-xl group-hover/card:ring-2 group-hover/card:ring-blue-200 transition-all duration-300">
                              <ProfessionalOrgFallback 
                                orgName={org.orgName || org.name} 
                                orgId={org.uid || `org-${index}`} 
                                variant="square" 
                                className="w-full h-full" 
                                sector={org.industry || org.careerCluster}
                              />
                            </div>
                          )}

                          {/* Hover External Link Badge */}
                          <div className="absolute top-2 right-2 bg-slate-900/85 backdrop-blur-xs text-white p-1.5 rounded-full opacity-0 group-hover/card:opacity-100 transition-all duration-200 shadow-md transform group-hover/card:scale-110">
                            <ExternalLink className="w-3.5 h-3.5 text-[#b9e612]" />
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* FOOTER WRAPPER */}
            <footer 
              className="w-full text-white py-6 md:py-8 px-6 md:px-12 flex flex-col items-center relative z-20 text-center"
              style={{ backgroundColor: activeTenant?.id ? getRegionDarkBg(activeTenant.id) : '#03142c' }}
            >
              {/* EDGE CENTER LOGO */}
              <div className="flex items-center justify-center select-none mb-4">
                {activeTenant?.logos?.navigation || globalBranding?.logos?.navigation ? (
                  <img 
                    src={activeTenant?.logos?.navigation || globalBranding?.logos?.navigation} 
                    alt={`${activeTenant?.name || 'Regional'} WBL Logo`} 
                    className="h-12 max-w-[300px] object-contain transition-all duration-300 hover:scale-105"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="flex items-baseline font-black leading-none text-white text-4xl md:text-5xl tracking-tight uppercase font-sans">
                    <span>EDG</span>
                    <span className="relative inline-block text-white">
                      E
                      <span className="absolute bottom-[2px] right-0 left-[55%] h-[15%] bg-[#b9e612]" />
                    </span>
                  </div>
                )}
              </div>

              {/* BRAND PARAGRAPH */}
              <p className="max-w-2xl mx-auto text-slate-350 text-xs md:text-sm font-medium leading-relaxed mb-6">
                Bridging the gap between classroom and career. Empowering South Carolina's
                future workforce through professional mentorship and high-impact work-based
                learning.
              </p>

              {/* LINK ROWS */}
              <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mb-6">
                <a 
                  href="#help" 
                  className="flex items-center space-x-1.5 text-[10px] font-black uppercase tracking-widest text-white hover:text-[#b9e612] transition-colors"
                  onClick={(e) => { 
                    e.preventDefault(); 
                    showAlert({
                      title: "Help & Documentation",
                      message: "Help desk documents and onboarding videos are accessible once authenticated into your regional workspace.",
                      type: "info"
                    }); 
                  }}
                >
                  <HelpCircle className="w-3.5 h-3.5 text-[#0094c8]" />
                  <span>Help & Tutorials</span>
                </a>
                <a 
                  href="#contact" 
                  className="flex items-center space-x-1.5 text-[10px] font-black uppercase tracking-widest text-white hover:text-[#b9e612] transition-colors"
                  onClick={(e) => { 
                    e.preventDefault(); 
                    setContactName('');
                    setContactEmail('');
                    setContactSubject('');
                    setContactMessage('');
                    setInquirySubmitted(false);
                    setInquiryError('');
                    setShowContactModal(true); 
                  }}
                >
                  <MessageSquare className="w-3.5 h-3.5 text-[#0094c8]" />
                  <span>Contact Us</span>
                </a>
                <a 
                  href="#terms" 
                  className="flex items-center space-x-1.5 text-[10px] font-black uppercase tracking-widest text-white hover:text-[#b9e612] transition-colors"
                  onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
                >
                  <Scale className="w-3.5 h-3.5 text-[#0094c8]" />
                  <span>Terms of Service</span>
                </a>
                <a 
                  href="#privacy" 
                  className="flex items-center space-x-1.5 text-[10px] font-black uppercase tracking-widest text-white hover:text-[#b9e612] transition-colors"
                  onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }}
                >
                  <ShieldCheck className="w-3.5 h-3.5 text-[#0094c8]" />
                  <span>Privacy Policy</span>
                </a>
                <a 
                  href="#license" 
                  className="flex items-center space-x-1.5 text-[10px] font-black uppercase tracking-widest text-white hover:text-[#b9e612] transition-colors"
                  onClick={(e) => { e.preventDefault(); setShowLicenseModal(true); }}
                >
                  <ShieldCheck className="w-3.5 h-3.5 text-[#0094c8]" />
                  <span>License Attribution</span>
                </a>
              </div>

              {/* DIVIDER */}
              <div className="w-full max-w-6xl border-t border-slate-700/40 mb-5" />

              {/* BOTTOM META ROW */}
              <div className="w-full max-w-6xl flex flex-col md:flex-row items-center justify-between gap-4 text-left">
                {/* LEFT CONTENT: Brand badge & Pill */}
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  {/* CIRCULAR BRAND BADGE */}
                  <div className="w-8 h-8 rounded-full bg-black/60 border border-slate-800 flex items-center justify-center shrink-0 shadow-xl select-none group hover:scale-105 transition-transform duration-300">
                    <span className="text-white text-sm font-black tracking-tighter">N</span>
                  </div>

                  {/* PILL FOR ACTIVE RESIDENT COUNTY */}
                  <div className="flex items-center space-x-2 bg-[#091e3a]/80 border border-slate-800/80 rounded-full px-3.5 py-1.5 shrink-0">
                    <span className="w-2 h-2 rounded-full bg-[#b9e612] animate-pulse shrink-0" />
                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-300">
                      Active Regional Tenant: <span className="text-[#b9e612] uppercase font-extrabold font-mono">{(activeTenant?.id || 'spartanburg').toUpperCase()}</span>
                    </span>
                  </div>
                </div>

                {/* RIGHT CONTENT: COPYRIGHT */}
                <div className="text-[10px] font-bold text-slate-400 tracking-wide uppercase text-center md:text-right">
                  © 2026 WBL Portal. All Rights Reserved. Built by <span className="text-white font-extrabold hover:underline cursor-pointer">LOSONOVA LLC</span>.
                </div>
              </div>
            </footer>
          </>
        )}

      </main>

      <AnimatePresence>
        {isDrawerOpen && (
            <>
                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setDrawerOpen(false)}
                    className="fixed inset-0 bg-black/40 z-40"
                />
                <motion.div
                    initial={{ x: '100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '100%' }}
                    transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                    className="fixed top-0 right-0 h-full w-[480px] bg-white z-50 shadow-2xl p-6 text-slate-900 overflow-y-auto"
                >
                    {drawerMode === 'signup' ? (
                        <SetupWizard initialRole={initialSignupRole} onComplete={() => { setDrawerOpen(false); setInitialSignupRole(null); }} />
                    ) : (
                        <>
                            <div className="flex justify-end mb-6">
                                <button 
                                    type="button" 
                                    onClick={() => setDrawerOpen(false)} 
                                    className="text-slate-400 hover:text-slate-600 transition-colors p-1.5 rounded-lg hover:bg-slate-50 cursor-pointer"
                                    aria-label="Close panel"
                                >
                                    <X className="w-5 h-5"/>
                                </button>
                            </div>

                            <div className="space-y-6">
                                {authError && (
                                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-start gap-2 shadow-sm animate-fade-in">
                                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                    <p className="font-medium leading-tight">{authError}</p>
                                  </div>
                                )}

                                <button 
                                    type="button"
                                    onClick={handleGoogleAuth} 
                                    disabled={isSubmitting}
                                    className="w-full bg-white text-slate-705 hover:bg-slate-50 border border-slate-200 rounded-lg py-3 px-4 font-black uppercase text-[10px] tracking-widest flex items-center justify-center space-x-2.5 transition-all shadow-2xs hover:shadow-xs cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                        <path d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.08H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.92l2.85-2.22-.19-.6z" fill="#FBBC05" />
                                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.08l3.66 2.84c.87-2.6 3.3-4.54 6.16-4.54z" fill="#EA4335" />
                                    </svg>
                                    <span>SIGN IN WITH GOOGLE</span>
                                </button>

                                <div className="relative flex items-center">
                                    <div className="flex-grow border-t border-slate-200"></div>
                                    <span className="flex-shrink mx-4 text-slate-400 text-[10px] font-black uppercase tracking-widest leading-none">OR SIGN IN WITH EMAIL</span>
                                    <div className="flex-grow border-t border-slate-200"></div>
                                </div>

                                <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); handleAuth(); }}>
                                    <div className="space-y-1.5 text-left">
                                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">EMAIL ADDRESS</label>
                                        <input 
                                            type="email" 
                                            placeholder="yourname@example.com" 
                                            value={email}
                                            required
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full px-4 py-3 bg-[#edf4fc] text-slate-800 font-bold border border-transparent rounded-lg focus:bg-white focus:border-[#0091cb] focus:ring-1 focus:ring-[#0091cb] outline-none text-xs transition-all shadow-2xs"
                                        />
                                    </div>
                                    
                                    <div className="space-y-1.5 text-left">
                                        <div className="flex justify-between items-center">
                                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider block animate-pulse">PASSWORD</label>
                                            <button 
                                                type="button" 
                                                className="text-[10px] font-black text-[#0091cb] hover:underline cursor-pointer uppercase tracking-wider" 
                                                style={{ color: globalBranding?.palette?.lightMain || '#0094c8' }}
                                                onClick={() => {
                                                    if ((window as any).smoothNavigate) {
                                                        (window as any).smoothNavigate('/forgot-password');
                                                    } else {
                                                        window.location.href = '/forgot-password';
                                                    }
                                                }}
                                            >
                                                Forgot?
                                            </button>
                                        </div>
                                        <div className="relative">
                                            <input 
                                                type={showPassword ? "text" : "password"} 
                                                placeholder="••••••••••••" 
                                                value={password}
                                                required
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="w-full pl-4 pr-11 py-3 bg-[#edf4fc] text-slate-800 font-bold border border-transparent rounded-lg focus:bg-white focus:border-[#0091cb] focus:ring-1 focus:ring-[#0091cb] outline-none text-xs transition-all shadow-2xs"
                                            />
                                            <button 
                                                type="button" 
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer p-1 rounded-md"
                                                aria-label={showPassword ? "Hide password" : "Show password"}
                                            >
                                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <button 
                                        type="submit"
                                        disabled={isSubmitting}
                                        style={{ backgroundColor: globalBranding?.palette?.lightMain || '#0094c8' }}
                                        className="w-full text-white rounded-lg py-3.5 font-black uppercase tracking-wider text-xs transition-all shadow-md hover:brightness-110 hover:scale-[1.01] active:scale-98 cursor-pointer flex items-center justify-center border-0 mt-2 disabled:opacity-75 disabled:cursor-not-allowed"
                                        id="auth-submit-btn"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> LOGGING IN...
                                            </>
                                        ) : (
                                            "LOG IN"
                                        )}
                                    </button>
                                </form>

                                <p className="text-[11px] text-center text-slate-400 mt-6 font-bold uppercase tracking-wider">
                                    Don't have an account? <button type="button" onClick={() => setDrawerMode('signup')} className="font-extrabold hover:underline" style={{ color: globalBranding?.palette?.lightMain || '#0094c8' }}>Sign Up</button>
                                </p>
                            </div>
                        </>
                    )}
                </motion.div>
             </>
        )}
      </AnimatePresence>

      {/* Privacy Policy Modal */}
      <AnimatePresence>
        {showPrivacyModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowPrivacyModal(false)}
              className="absolute inset-0 bg-[#020d1a]/85 backdrop-blur-md"
            />
            
            {/* Modal Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.4 }}
              className="relative w-full max-w-3xl bg-[#091526] border border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] z-10"
            >
              {/* Header */}
              <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-black/20 shrink-0">
                <div className="flex items-center space-x-2.5">
                  <ShieldCheck className="w-5 h-5 text-[#b9e612]" />
                  <h3 className="text-sm font-black text-white uppercase tracking-widest">
                    WBL PORTAL: PRIVACY POLICY
                  </h3>
                </div>
                <button
                  onClick={() => setShowPrivacyModal(false)}
                  className="p-1.5 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition-colors cursor-pointer"
                  title="Close Legal Document"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Body (scrollable) */}
              <div className="p-6 md:p-8 overflow-y-auto text-slate-300 text-xs md:text-sm leading-relaxed space-y-6 antialiased text-left">
                <div className="flex items-center justify-between border-b border-slate-700/20 pb-4 mb-2">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-[#b9e612]">Official Disclosure Protocol</span>
                  <span className="text-[10px] font-mono text-slate-400">Effective Date: May 1, 2026</span>
                </div>

                <p className="font-semibold text-slate-200">
                  The WBL Portal recognizes that professional data is personal. This policy outlines how we collect, protect, and share information within our regional Work-Based Learning (WBL) ecosystem.
                </p>

                {/* 1. Our Role */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">1</span>
                    Our Role: The Data Processor
                  </h4>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">Platform Service:</strong> WBL Portal provides the Platform as a Service (SaaS) to Sponsoring Entities, such as Regional or State Chambers of Commerce.</li>
                    <li><strong className="text-slate-200">FERPA Authority:</strong> For purposes of the Family Educational Rights and Privacy Act (FERPA), each participating School District remains the educational agency of record and retains sole legal responsibility for student education records.</li>
                    <li><strong className="text-slate-200">Data Stewardship:</strong> The Sponsoring Entity and your associated School District act as the stewards of your data and as data controllers for student education records.</li>
                    <li><strong className="text-slate-200">Legal Status:</strong> WBL Portal acts solely as a data processor and school official with a legitimate educational interest, performing functions on behalf of participating School Districts pursuant to written agreements.</li>
                    <li><strong className="text-slate-200">No Sale of Data:</strong> WBL Portal does not sell, rent, or trade your Personally Identifiable Information (PII) to third-party marketers or data brokers.</li>
                  </ul>
                </div>

                {/* 2. Information We Collect */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">2</span>
                    Information We Collect
                  </h4>
                  <p className="pl-7 text-slate-350 text-xs">
                    We collect information only to facilitate the professional connection between students and employers:
                  </p>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">Students:</strong> Name, email, phone, date of birth, school and district affiliation, graduation year, career interests (SC Clusters), resumes, and professional summaries.</li>
                    <li><strong className="text-slate-200">Advocates (Parents):</strong> Name, email, and linking credentials to a minor student.</li>
                    <li><strong className="text-slate-200">Employers/Advisors:</strong> Professional contact details, organization info, and job/placement requirements.</li>
                    <li><strong className="text-slate-200">Automated Data:</strong> IP addresses, device types, and portal activity logs (for the "Activity Tracker" feature).</li>
                  </ul>
                </div>

                {/* 3. The FERPA "Consent Gate" */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">3</span>
                    The FERPA "Consent Gate" & Disclosure
                  </h4>
                  <p className="pl-7 text-slate-350 text-xs">
                    The sharing of Student PII is strictly controlled by Consent Status:
                  </p>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">Minors (13-17):</strong> No data is visible to Employers or the Sponsoring Entity until an Advocate toggles "Grant Consent" in the Legal Authorizations tab.</li>
                    <li><strong className="text-slate-200">Adults (18+):</strong> The student manages their own consent.</li>
                    <li><strong className="text-slate-200">Exceptions:</strong> Authorized School Advisors and designated Regional Administrators may access the minimum student information necessary to verify eligibility, oversee placements, meet reporting requirements, and administer school-authorized programs. <em className="text-slate-400 font-medium font-sans">Note: This excludes resumes, evaluations, and employer feedback unless separately authorized.</em></li>
                    <li><strong className="text-slate-200">Scope of Consent:</strong> Consent authorizes disclosure only for work-based learning placement, interview, evaluation, and program administration purposes.</li>
                  </ul>
                </div>

                {/* 4. How We Use */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">4</span>
                    How We Use Your Information
                  </h4>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">Placement Facilitation:</strong> To support matching students with employer opportunities approved by participating School Districts.</li>
                    <li><strong className="text-slate-200">Oslo AI Processing:</strong> AI may be used to generate draft evaluations or summaries. <em className="text-slate-400 font-medium font-sans">Student PII is masked during AI processing. Data is not used to train any global or third-party models. AI outputs are visible only to authorized school officials and the originating employer.</em></li>
                    <li><strong className="text-slate-200">Regional Analytics:</strong> Chambers may receive aggregate, de-identified reports reflecting workforce trends. No student-level data is included.</li>
                  </ul>
                </div>

                {/* 5. Your Rights */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">5</span>
                    Your Rights: Access and "Instant Revocation"
                  </h4>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">Right to Inspect and Correct:</strong> Students and parents retain FERPA rights to inspect and request correction of education records. Identification data verified by schools must be corrected through authorized administrators. Data initially populated by the student must be corrected through the WBL Portal administrator; all other data can be edited via the User Profile.</li>
                    <li><strong className="text-slate-200">Right to Revoke Consent:</strong> Consent may be withdrawn at any time. Upon revocation, student information is masked prospectively from employer-facing systems. Revocation does not require the recall of information already lawfully disclosed during an active consent period.</li>
                    <li><strong className="text-slate-200">Transition at Age 18:</strong> Upon a student turning 18, FERPA rights automatically transfer from the Advocate to the student.</li>
                  </ul>
                </div>

                {/* 6. Security & Retention */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">6</span>
                    Data Security & Retention
                  </h4>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">Security:</strong> Data is encrypted in transit and at rest using industry-standard security protocols.</li>
                    <li><strong className="text-slate-200">Retention:</strong> Student data is retained only for as long as directed by the participating School District. WBL Portal does not independently determine public-records retention obligations.</li>
                    <li><strong className="text-slate-200">Account Deletion:</strong> Upon authorized deletion, PII is removed from active production systems within 30 days. De-identified logs may be retained for compliance and reporting purposes.</li>
                  </ul>
                </div>

                {/* 7. Third-Party Integrations */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">7</span>
                    Third-Party Integrations
                  </h4>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">Virtual Meetings:</strong> Interviews conducted via third-party platforms (e.g., Zoom, Teams, Google Meet) are subject to those providers' privacy policies.</li>
                    <li><strong className="text-slate-200">Calendar Sync:</strong> Calendar integrations request only the permissions necessary to schedule interviews.</li>
                  </ul>
                </div>

                {/* 8. COPPA */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">8</span>
                    COPPA Compliance (Children Under 13)
                  </h4>
                  <p className="pl-7 text-slate-350 text-xs">
                    WBL Portal is not intended for children under 13. If we discover an account has been created for a child under 13, it will be terminated immediately.
                  </p>
                </div>

                {/* 9. Changes */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">9</span>
                    Changes to this Policy
                  </h4>
                  <p className="pl-7 text-slate-350 text-xs">
                    Material changes affecting student privacy or data sharing will be communicated to users and participating School Districts. Continued use of the portal constitutes acceptance of updated terms.
                  </p>
                </div>

                {/* 10. Contact Us */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">10</span>
                    Contact Us
                  </h4>
                  <p className="pl-7 text-slate-350 text-xs">
                    For privacy-related inquiries, contact the WBL Portal Privacy Office at <a href="mailto:admin@wblportal.com" className="text-[#0094c8] hover:underline font-extrabold font-mono">admin@wblportal.com</a>.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-slate-800 bg-black/40 text-center shrink-0">
                <button
                  type="button"
                  onClick={() => setShowPrivacyModal(false)}
                  style={{ backgroundColor: globalBranding?.palette?.lightMain || '#0094c8' }}
                  className="px-6 py-2.5 text-white rounded-xl font-black uppercase text-[11px] tracking-widest transition-all hover:scale-[1.02] active:scale-98 cursor-pointer shadow-md border-0"
                >
                  Document Acknowledged
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Terms of Service Modal */}
      <AnimatePresence>
        {showTermsModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowTermsModal(false)}
              className="absolute inset-0 bg-[#020d1a]/85 backdrop-blur-md"
            />
            
            {/* Modal Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.4 }}
              className="relative w-full max-w-3xl bg-[#091526] border border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] z-10"
            >
              {/* Header */}
              <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-black/20 shrink-0">
                <div className="flex items-center space-x-2.5">
                  <Scale className="w-5 h-5 text-[#b9e612]" />
                  <h3 className="text-sm font-black text-white uppercase tracking-widest">
                    WBL PORTAL: TERMS OF SERVICE
                  </h3>
                </div>
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="p-1.5 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition-colors cursor-pointer"
                  title="Close Legal Document"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Body (scrollable) */}
              <div className="p-6 md:p-8 overflow-y-auto text-slate-300 text-xs md:text-sm leading-relaxed space-y-6 antialiased text-left">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 border-b border-slate-700/20 pb-4 mb-2">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-[#b9e612]">South Carolina Work-Based Learning Portal</span>
                  <div className="flex flex-col sm:items-end text-[10px] font-mono text-slate-400">
                    <span>Effective Date: May 1, 2026</span>
                    <span>Governing Law: South Carolina, USA</span>
                  </div>
                </div>

                {/* 1. Definitions */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">1</span>
                    Definitions & The WBL Portal Ecosystem
                  </h4>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">"The Platform"</strong> refers to the work-based learning infrastructure at wblportal.com, including all regional county sub-portals.</li>
                    <li><strong className="text-slate-200">"Sponsoring Entity"</strong> refers to the Regional or State Chamber of Commerce (e.g., OneSpartanburg Inc. or the SC Department of Employment and Workforce) that licenses the Platform.</li>
                    <li><strong className="text-slate-200">"AI Feature"</strong> means any component employing machine learning, specifically the Oslo AI Assistant.</li>
                    <li><strong className="text-slate-200">"Advocate"</strong> means the parent or legal guardian linked to a minor student’s account under the 2026 Age-Appropriate Design Code requirements.</li>
                    <li><strong className="text-slate-200">"User"</strong> refers to any student, parent, employer, or advisor accessing the Services.</li>
                  </ul>
                </div>

                {/* 2. Eligibility */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">2</span>
                    Eligibility & Account Security
                  </h4>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">Age Requirements:</strong> You must be at least 13 years old. In compliance with SC HB 3431 (2026), users aged 13–17 must have a linked Advocate account to activate data-sharing features.</li>
                    <li><strong className="text-slate-200">Verification:</strong> Advisor and Employer accounts are subject to manual verification by the Sponsoring Entity. WBL Portal reserves the right to refuse or forfeit any username or account at its sole discretion if professional or educational intent cannot be verified.</li>
                    <li><strong className="text-slate-200">Account Integrity:</strong> You are prohibited from creating accounts through unauthorized means (scripts, bots, crawlers) or maintaining more than one account per role.</li>
                  </ul>
                </div>

                {/* 3. Data Ownership */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">3</span>
                    Data Ownership & Limited License
                  </h4>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">User Content:</strong> You retain ownership of all resumes, profiles, and media ("User Content") you upload.</li>
                    <li><strong className="text-slate-200">License Grant:</strong> You grant WBL Portal and the Sponsoring Entity a non-exclusive, sublicensable, worldwide, royalty-free license to use, copy, and distribute your content only in connection with operating the Work-Based Learning (WBL) program.</li>
                    <li><strong className="text-slate-200">Proprietary Process Logic:</strong> The specific staged-visibility logic (Student → Parent → Advisor → Employer) and the "Kill-Switch" architecture are Protected Trade Secrets of the WBL Portal. You agree not to replicate, decompile, or reverse-engineer these proprietary business processes.</li>
                  </ul>
                </div>

                {/* 4. FERPA Compliance */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">4</span>
                    FERPA Compliance & The "Consent Gate"
                  </h4>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">Consent Mandate:</strong> For students under 18, no Personally Identifiable Information (PII) is shared with Employers until the Advocate grants explicit FERPA consent. For students 18+, the student provides their own consent.</li>
                    <li><strong className="text-slate-200">Instant Revocation:</strong> If consent is revoked in the "Legal Authorizations" tab, WBL Portal’s "Kill-Switch" architecture terminates digital access to the student’s profile by Employers and Advisors within the current active server session.</li>
                    <li><strong className="text-slate-200">18th Birthday Transition:</strong> On the day a student turns 18, the Advocate’s legal authority to manage consent expires. The student must re-authorize data-sharing. The Advocate account reverts to "Read-Only" unless the student provides new authorization.</li>
                  </ul>

                  {/* Section 4.2: LosoNova LLC Vendor Protection & District License Agreement */}
                  <div className="mt-4 p-4 rounded-xl bg-slate-900/80 border border-slate-700/60 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-wider text-[#b9e612]">
                        4.2 Platform Terms of Service & District License Agreement (Vendor Protection)
                      </span>
                      <span className="text-[9px] font-mono text-slate-400">
                        LosoNova LLC &bull; E-SIGN / UETA
                      </span>
                    </div>
                    <ul className="space-y-2 text-slate-300 text-xs">
                      <li>
                        <strong className="text-white">Accuracy Disclaimer:</strong> LosoNova LLC provides electronic delivery and workflow management software and relies strictly on the affirmative representations, certifications, and attestations made by individual user signers (students, parents, legal guardians, and educational advisors) under the Electronic Signatures in Global and National Commerce Act (E-SIGN, 15 U.S.C. § 7001) and Uniform Electronic Transactions Act (UETA, S.C. Code Ann. § 26-6-10).
                      </li>
                      <li>
                        <strong className="text-white">No Independent Audit Obligation:</strong> The platform and LosoNova LLC do not independently audit, investigate, or verify underlying familial or legal guardianship relationships. Liability for fraudulent, fabricated, or deceptive information submitted by students or third parties rests solely on the individual user making the false representation. School districts, sponsoring regional chambers, and LosoNova LLC are held harmless from any civil, administrative, or statutory liability arising from intentional misrepresentation or fraudulent attestation by users.
                      </li>
                    </ul>
                  </div>
                </div>

                {/* 5. Oslo AI */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">5</span>
                    Oslo AI & Generative Content
                  </h4>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">HITL (Human-in-the-Loop):</strong> Oslo AI generates preliminary evaluation drafts. In compliance with 2026 safety standards, Employers are solely responsible for reviewing and editing AI-generated output for accuracy before applying a digital signature.</li>
                    <li><strong className="text-slate-200">AI Disclaimer:</strong> WBL Portal does not warrant that AI-generated responses will be factually accurate. Your reliance on AI Output is at your own risk.</li>
                    <li><strong className="text-slate-200">Prohibited AI Use:</strong> You may not use the Platform or its output to develop, train, or benchmark competing machine learning models.</li>
                  </ul>
                </div>

                {/* 6. Employer Covenants */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">6</span>
                    Employer Covenants & Equitable Recruitment
                  </h4>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">Stewardship:</strong> Employers agree to use student data only for the specific opportunity applied for. Employers shall not sell or disclose data for marketing, data monetization, or advertising.</li>
                    <li><strong className="text-slate-200">Fairness:</strong> Employers agree to maintain a fair and equitable recruitment process and shall not discriminate as prohibited by South Carolina and Federal law.</li>
                  </ul>
                </div>

                {/* 7. Limitation of Liability */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">7</span>
                    Limitation of Liability
                  </h4>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">Platform Role:</strong> WBL Portal is a passive conduit. We are not the employer of any student and are not a party to any employment contract.</li>
                    <li><strong className="text-slate-200">Warranty Disclaimer:</strong> The Service is provided "AS IS." WBL Portal disclaims all warranties of merchantability and fitness for a particular purpose.</li>
                    <li><strong className="text-slate-200">Liability Cap:</strong> To the fullest extent permitted by law, the total liability of the "WBL Portal Parties" (including employees and Sponsoring Chambers) for any claim shall not exceed $100.00 USD.</li>
                  </ul>
                </div>

                {/* 8. Indemnification */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">8</span>
                    Indemnification
                  </h4>
                  <p className="pl-7 text-slate-400">
                    Employers agree to defend, indemnify, and hold harmless WBL Portal, the Sponsoring Entity, and their respective officers, directors, employees, contractors, and agents (collectively, the “WBL Portal Parties”) from and against any claims, damages, liabilities, losses, and expenses (including reasonable attorneys’ fees) arising out of or related to:
                  </p>
                  <ul className="space-y-1.5 pl-12 list-disc list-outside text-slate-400 text-xs">
                    <li>Employer’s use or misuse of Student data;</li>
                    <li>Employer’s violation of applicable law, including employment, anti-discrimination, wage and hour, or data protection laws;</li>
                    <li>Employer’s reliance on or implementation of AI-generated output; or</li>
                    <li>Employer’s interactions with Students outside the Platform.</li>
                  </ul>
                  <p className="pl-7 text-slate-400 text-xs font-semibold">
                    This obligation survives termination of these Terms.
                  </p>
                </div>

                {/* 9. Safety */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">9</span>
                    Physical Safety & Insurance
                  </h4>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">No Vetting:</strong> WBL Portal does not perform criminal background checks or workplace safety audits.</li>
                    <li><strong className="text-slate-200">Assumption of Risk:</strong> Users assume all risks associated with interacting with other users offline. Liability for workplace injuries is governed strictly by the separate Tri-Party Training Agreement.</li>
                  </ul>
                </div>

                {/* 10. Scraping */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">10</span>
                    Prohibited Conduct & Scraping
                  </h4>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">Anti-Scraping:</strong> You may not use automated scripts to bulk-collect data from the portal.</li>
                    <li><strong className="text-slate-200">Non-Circumvention:</strong> You may not use the Services or any proprietary logic obtained from them to develop or support a product that competes with WBL Portal.</li>
                  </ul>
                </div>

                {/* 11. Governing Law */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">11</span>
                    Governing Law & Arbitration
                  </h4>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">Governing Law:</strong> These Terms are governed by the laws of the State of South Carolina.</li>
                    <li><strong className="text-slate-200">Arbitration:</strong> Any dispute arising out of or relating to these Terms or the Services shall be resolved by binding arbitration administered by the American Arbitration Association (AAA) in accordance with its Commercial Arbitration Rules. Arbitration shall take place in the county of the Sponsoring Entity, South Carolina.</li>
                    <li><strong className="text-slate-200">Relief:</strong> The arbitrator may award only individual relief and may not consolidate claims or award class-wide remedies. Each party bears its own attorneys’ fees unless otherwise required by law or awarded by the arbitrator.</li>
                    <li><strong className="text-slate-200">Class Action Waiver:</strong> You agree to resolve disputes on an individual basis only.</li>
                  </ul>
                </div>

                {/* 12. Miscellaneous */}
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#0094c8]/25 text-[#0094c8] flex items-center justify-center text-[10px] font-bold">12</span>
                    Miscellaneous
                  </h4>
                  <ul className="space-y-2.5 pl-7 list-disc list-outside text-slate-400">
                    <li><strong className="text-slate-200">Assignment:</strong> WBL Portal may assign these Terms in connection with a merger, reorganization, or transfer of the Platform. Users may not assign these Terms without prior written consent.</li>
                    <li><strong className="text-slate-200">Severability:</strong> If any provision is held unenforceable, the remaining provisions shall remain in full force and effect.</li>
                    <li><strong className="text-slate-200">No Waiver:</strong> Failure to enforce any provision shall not constitute a waiver.</li>
                  </ul>
                </div>

                <div className="border-t border-slate-700/30 pt-4 mt-6 text-center">
                  <p className="text-[10px] text-slate-400 italic">
                    By clicking "I Accept" or accessing the Service, you provide an electronic signature under the Uniform Electronic Transactions Act (UETA) and agree to be bound by these Terms.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-slate-800 bg-black/40 text-center shrink-0">
                <button
                  type="button"
                  onClick={() => setShowTermsModal(false)}
                  style={{ backgroundColor: globalBranding?.palette?.lightMain || '#0094c8' }}
                  className="px-6 py-2.5 text-white rounded-xl font-black uppercase text-[11px] tracking-widest transition-all hover:scale-[1.02] active:scale-98 cursor-pointer shadow-md border-0"
                >
                  I Accept & Agree
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Contact Us Support & Inquiries Modal */}
      <AnimatePresence>
        {showContactModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowContactModal(false)}
              className="absolute inset-0 bg-[#020d1a]/85 backdrop-blur-md"
            />
            
            {/* Modal Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.4 }}
              className="relative w-full max-w-4xl bg-[#091526] border border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] z-10"
            >
              {/* Header */}
              <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-black/20 shrink-0">
                <div className="flex items-center space-x-2.5">
                  <MessageSquare className="w-5 h-5 text-[#b9e612]" />
                  <h3 className="text-sm font-black text-white uppercase tracking-widest">
                    Contact Us Support & Inquiries
                  </h3>
                </div>
                <button
                  onClick={() => setShowContactModal(false)}
                  className="p-1.5 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition-colors cursor-pointer"
                  title="Close Form"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 md:p-8 overflow-y-auto flex-1">
                {inquirySubmitted ? (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center justify-center text-center py-12 space-y-4"
                  >
                    <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 mb-2">
                      <CheckCircle2 className="w-8 h-8" />
                    </div>
                    <h4 className="text-lg font-black text-white uppercase tracking-wider">Inquiry Submitted Successfully</h4>
                    <p className="text-sm text-slate-300 max-w-md leading-relaxed">
                      Thank you for reaching out, <span className="text-[#b9e612] font-semibold">{contactName}</span>! Your message has been received and routed to our regional administration desk in Spartanburg County.
                    </p>
                    <p className="text-xs text-slate-400">
                      A copy of your inquiry details has been logged in our secure processing logs.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowContactModal(false)}
                      style={{ backgroundColor: globalBranding?.palette?.lightMain || '#0094c8' }}
                      className="mt-6 px-6 py-2.5 text-white rounded-xl font-black uppercase text-[11px] tracking-widest transition-all hover:scale-[1.02] active:scale-98 cursor-pointer shadow-md border-0"
                    >
                      Close Window
                    </button>
                  </motion.div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                    {/* Left details pane */}
                    <div className="md:col-span-2 space-y-6 text-left border-b md:border-b-0 md:border-r border-slate-800 pb-6 md:pb-0 md:pr-8">
                      <div>
                        <span className="text-[10px] uppercase font-bold tracking-widest text-[#b9e612]">WBL Support Desk</span>
                        <h4 className="text-xl font-black text-white mt-1 mb-3">Get in Touch</h4>
                        <p className="text-slate-300 text-xs md:text-sm leading-relaxed font-medium">
                          Have questions about Work-Based Learning in Spartanburg County? Our team is here to help students, employers, and advisors.
                        </p>
                      </div>

                      <div className="space-y-4 pt-4 border-t border-slate-800/60">
                        <div className="flex items-start space-x-3 text-xs">
                          <Mail className="w-4 h-4 text-[#0094c8] shrink-0 mt-0.5" />
                          <div>
                            <p className="font-bold text-white uppercase tracking-wider text-[9px]">Direct Email Channel</p>
                            <a href="mailto:admin@wblportal.com" className="text-slate-400 hover:text-white transition-colors font-mono">
                              admin@wblportal.com
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Form pane */}
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      if (!contactName.trim() || !contactEmail.trim() || !contactSubject.trim() || !contactMessage.trim()) {
                        setInquiryError('All fields are required.');
                        return;
                      }
                      setIsSubmittingInquiry(true);
                      setInquiryError('');
                      try {
                        const payload = {
                          name: contactName,
                          email: contactEmail,
                          subject: contactSubject,
                          message: contactMessage,
                          createdAt: new Date().toISOString()
                        };
                        await addDoc(collection(db, 'support_inquiries'), payload);
                        setInquirySubmitted(true);
                      } catch (err: any) {
                        console.error('Error submitting Support Inquiry:', err);
                        setInquiryError(err.message || 'Failed to submit support inquiry. Please try again later.');
                      } finally {
                        setIsSubmittingInquiry(false);
                      }
                    }} className="md:col-span-3 space-y-4 text-left">
                      <div>
                        <h4 className="text-sm font-black text-white uppercase tracking-wider">Send a Message</h4>
                        <p className="text-[11px] text-slate-400 leading-normal mt-0.5">
                          Fill out the form below and we will route your inquiry to the correct department.
                        </p>
                      </div>

                      {inquiryError && (
                        <div className="p-3 bg-red-500/10 border border-red-500/25 rounded-xl flex items-start space-x-2 text-xs text-red-400">
                          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                          <span>{inquiryError}</span>
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Name */}
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 block">
                            Your Name
                          </label>
                          <input
                            type="text"
                            required
                            placeholder="Jane Doe"
                            value={contactName}
                            onChange={(e) => setContactName(e.target.value)}
                            disabled={isSubmittingInquiry}
                            className="w-full bg-[#030912]/80 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-[#0094c8] focus:ring-1 focus:ring-[#0094c8]/30 transition-all font-medium"
                          />
                        </div>

                        {/* Email */}
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 block">
                            Email Address
                          </label>
                          <input
                            type="email"
                            required
                            placeholder="jane@example.com"
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            disabled={isSubmittingInquiry}
                            className="w-full bg-[#030912]/80 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-[#0094c8] focus:ring-1 focus:ring-[#0094c8]/30 transition-all font-medium"
                          />
                        </div>
                      </div>

                      {/* Subject */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 block">
                          Subject
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="How can we help you?"
                          value={contactSubject}
                          onChange={(e) => setContactSubject(e.target.value)}
                          disabled={isSubmittingInquiry}
                          className="w-full bg-[#030912]/80 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-[#0094c8] focus:ring-1 focus:ring-[#0094c8]/30 transition-all font-medium"
                        />
                      </div>

                      {/* Message */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 block">
                          Message
                        </label>
                        <textarea
                          required
                          rows={4}
                          placeholder="Describe your inquiry in detail..."
                          value={contactMessage}
                          onChange={(e) => setContactMessage(e.target.value)}
                          disabled={isSubmittingInquiry}
                          className="w-full bg-[#030912]/80 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-[#0094c8] focus:ring-1 focus:ring-[#0094c8]/30 transition-all font-medium resize-none leading-relaxed"
                        />
                      </div>

                      {/* Submit Button */}
                      <div className="pt-2 flex justify-end">
                        <button
                          type="submit"
                          disabled={isSubmittingInquiry}
                          style={{ backgroundColor: globalBranding?.palette?.lightMain || '#0094c8' }}
                          className="px-6 py-3 text-white rounded-xl font-black uppercase text-[11px] tracking-widest transition-all hover:scale-[1.02] active:scale-98 cursor-pointer shadow-md border-0 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                          {isSubmittingInquiry ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              <span>Submitting...</span>
                            </>
                          ) : (
                            <>
                              <Send className="w-3.5 h-3.5" />
                              <span>Submit Inquiry</span>
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
